import React, { useState, useEffect } from 'react';
import './MyActivities.css';
import ActivityCard from '../components/ActivityCard';
import AILoadingState from '../components/AILoadingState';
import { activityAssignmentsApi } from '../services/api';

const MyActivities = ({ user }) => {
  const [assignments, setAssignments] = useState([]);
  const [filteredAssignments, setFilteredAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showGuidanceModal, setShowGuidanceModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [aiGeneratingGuidance, setAiGeneratingGuidance] = useState(false);
  const [guidanceResult, setGuidanceResult] = useState(null);

  useEffect(() => {
    if (user) {
      console.log('User object:', user);
      console.log('User ID:', user?.id);
      loadAssignments();
    }
  }, [user]);

  useEffect(() => {
    applyFilter();
  }, [assignments, activeFilter]);

  const loadAssignments = async () => {
    try {
      setLoading(true);
      console.log('Loading assignments for user:', user);

      // Since the API now uses email-based lookup from JWT token,
      // the userId parameter doesn't matter. We can pass any ID or a dummy ID.
      const userId = user?.id || '00000000-0000-0000-0000-000000000000';

      const data = await activityAssignmentsApi.getUserAssignments(userId);
      console.log('Loaded assignments:', data);
      setAssignments(data);
    } catch (error) {
      console.error('Error loading assignments:', error);
      alert('Failed to load assignments: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = () => {
    if (activeFilter === 'all') {
      setFilteredAssignments(assignments);
    } else {
      const statusMap = {
        'pending': 1,
        'in-progress': 2,
        'completed': 3
      };
      setFilteredAssignments(assignments.filter(a => a.status === statusMap[activeFilter]));
    }
  };

  const handleStatusChange = async (assignmentId, newStatus) => {
    try {
      await activityAssignmentsApi.updateStatus(assignmentId, newStatus);
      await loadAssignments();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status: ' + error.message);
    }
  };

  const handleGetGuidance = (assignment) => {
    setSelectedAssignment(assignment);
    setGuidanceResult(null);
    setShowGuidanceModal(true);
    generateGuidance(assignment.id);
  };

  const generateGuidance = async (assignmentId) => {
    try {
      setAiGeneratingGuidance(true);
      const guidance = await activityAssignmentsApi.getPersonalizedGuidance(assignmentId);
      setGuidanceResult(guidance);
    } catch (error) {
      console.error('Error generating guidance:', error);
      alert('Failed to generate guidance: ' + error.message);
    } finally {
      setAiGeneratingGuidance(false);
    }
  };

  const getStatusCounts = () => {
    return {
      total: assignments.length,
      pending: assignments.filter(a => a.status === 1).length,
      inProgress: assignments.filter(a => a.status === 2).length,
      completed: assignments.filter(a => a.status === 3).length
    };
  };

  const statusCounts = getStatusCounts();

  if (loading) {
    return <div className="my-activities-loading">Loading your activities...</div>;
  }

  return (
    <div className="my-activities-container">
      <div className="my-activities-header">
        <h1>My Activities</h1>
      </div>

      {/* Summary Stats */}
      <div className="activity-summary-stats">
        <div className="stat-card">
          <div className="stat-value">{statusCounts.total}</div>
          <div className="stat-label">Total Activities</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{statusCounts.inProgress}</div>
          <div className="stat-label">In Progress</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{statusCounts.completed}</div>
          <div className="stat-label">Completed</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="activity-filter-tabs">
        <button
          className={`filter-tab ${activeFilter === 'all' ? 'active' : ''}`}
          onClick={() => setActiveFilter('all')}
        >
          All ({statusCounts.total})
        </button>
        <button
          className={`filter-tab ${activeFilter === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveFilter('pending')}
        >
          Pending ({statusCounts.pending})
        </button>
        <button
          className={`filter-tab ${activeFilter === 'in-progress' ? 'active' : ''}`}
          onClick={() => setActiveFilter('in-progress')}
        >
          In Progress ({statusCounts.inProgress})
        </button>
        <button
          className={`filter-tab ${activeFilter === 'completed' ? 'active' : ''}`}
          onClick={() => setActiveFilter('completed')}
        >
          Completed ({statusCounts.completed})
        </button>
      </div>

      {/* Activities Grid */}
      <div className="my-activities-grid">
        {filteredAssignments.length === 0 ? (
          <div className="no-activities">
            <p>
              {activeFilter === 'all'
                ? 'No activities assigned yet. Check back soon!'
                : `No ${activeFilter.replace('-', ' ')} activities.`}
            </p>
          </div>
        ) : (
          filteredAssignments.map((assignment) => (
            <ActivityCard
              key={assignment.id}
              activity={assignment}
              mode="user"
              onGuidanceClick={handleGetGuidance}
              onStatusChange={handleStatusChange}
            />
          ))
        )}
      </div>

      {/* Guidance Modal */}
      {showGuidanceModal && selectedAssignment && (
        <div className="modal-overlay" onClick={() => !aiGeneratingGuidance && setShowGuidanceModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Personalized Guidance</h2>
              {!aiGeneratingGuidance && (
                <button className="modal-close" onClick={() => setShowGuidanceModal(false)}>
                  ×
                </button>
              )}
            </div>
            <div className="modal-body">
              {aiGeneratingGuidance && (
                <AILoadingState
                  messages={[
                    "Analyzing your skills and the activity requirements...",
                    "Creating a personalized strategy for you...",
                    "Breaking down the activity into manageable steps...",
                    "Gathering resources and recommendations...",
                    "Finalizing your personalized guidance..."
                  ]}
                  interval={6000}
                />
              )}

              {guidanceResult && !aiGeneratingGuidance && (
                <div className="guidance-content">
                  <div className="activity-title-section">
                    <h3>{selectedAssignment.activityTask.name}</h3>
                  </div>

                  <div className="guidance-section">
                    <h4>Your Personalized Strategy</h4>
                    <p className="guidance-strategy">{guidanceResult.personalizedStrategy}</p>
                  </div>

                  {guidanceResult.steps && guidanceResult.steps.length > 0 && (
                    <div className="guidance-section">
                      <h4>Step-by-Step Guide</h4>
                      <div className="guidance-steps">
                        {guidanceResult.steps.map((step) => (
                          <div key={step.order} className="guidance-step">
                            <div className="step-number">{step.order}</div>
                            <div className="step-content">
                              <h5>{step.title}</h5>
                              <p>{step.description}</p>
                              {step.estimatedTimeHours && (
                                <div className="step-time">
                                  Estimated: {step.estimatedTimeHours} hours
                                </div>
                              )}
                              {step.requiredSkills && step.requiredSkills.length > 0 && (
                                <div className="step-skills">
                                  Skills: {step.requiredSkills.join(', ')}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {guidanceResult.tipsForUser && guidanceResult.tipsForUser.length > 0 && (
                    <div className="guidance-section">
                      <h4>Tips for Success</h4>
                      <ul className="guidance-list">
                        {guidanceResult.tipsForUser.map((tip, idx) => (
                          <li key={idx} className="tip-item">{tip}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {guidanceResult.resourceRecommendations && guidanceResult.resourceRecommendations.length > 0 && (
                    <div className="guidance-section">
                      <h4>Recommended Resources</h4>
                      <ul className="guidance-list">
                        {guidanceResult.resourceRecommendations.map((resource, idx) => (
                          <li key={idx} className="resource-item">{resource}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {guidanceResult.skillImprovementSuggestions && guidanceResult.skillImprovementSuggestions.length > 0 && (
                    <div className="guidance-section">
                      <h4>Skill Improvement Suggestions</h4>
                      <ul className="guidance-list">
                        {guidanceResult.skillImprovementSuggestions.map((suggestion, idx) => (
                          <li key={idx} className="suggestion-item">{suggestion}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
            {!aiGeneratingGuidance && (
              <div className="modal-footer">
                <button className="btn-primary" onClick={() => setShowGuidanceModal(false)}>
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyActivities;
